// hooks/useNotifications.js - Notification management hook
import { useState, useCallback, useEffect } from 'react';
import { NotificationService } from '../services/NotificationService';
import organizationCache from '../cache/OrganizationCache';
import logger from '../../../shared/services/logging/logger';

export const useNotifications = (userId) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Calculate unread count whenever notifications change
  useEffect(() => {
    const count = notifications.filter(notification => !notification.read).length;
    setUnreadCount(count);
  }, [notifications]);

  const fetchNotifications = useCallback(async (forceRefresh = false) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    // Check cache first unless forcing refresh
    if (!forceRefresh) {
      const cachedNotifications = organizationCache.get('notifications', userId);
      if (cachedNotifications) {
        const formattedNotifications = cachedNotifications.map(notification => {
          try {
            return NotificationService.formatNotificationForUI(notification);
          } catch (formatError) {
            return {
              ...notification,
              notificationId: notification.id || notification.notificationId,
              read: false
            };
          }
        });
        setNotifications(formattedNotifications);
        setLoading(false);
        return;
      }
    }

    // Check if already loading
    if (organizationCache.isLoading('notifications', userId)) {
      setLoading(true);
      return;
    }

    setLoading(true);
    setError(null);
    organizationCache.setLoading('notifications', userId, true);

    try {
      const notificationsData = await NotificationService.getAllNotifications(userId);

      // Cache the raw data
      organizationCache.set('notifications', userId, notificationsData);

      const formattedNotifications = notificationsData.map(notification => {
        try {
          return NotificationService.formatNotificationForUI(notification);
        } catch (formatError) {
          return {
            ...notification,
            notificationId: notification.id || notification.notificationId,
            read: false
          };
        }
      });

      setNotifications(formattedNotifications);
    } catch (err) {
      logger.error('Failed to fetch notifications', { error: err.message, userId });
      setError('Failed to load notifications. Please try again later.');
    } finally {
      setLoading(false);
      organizationCache.setLoading('notifications', userId, false);
    }
  }, [userId]);

  const fetchUnreadNotifications = useCallback(async (forceRefresh = false) => {
    if (!userId) {
      return;
    }

    try {
      const unreadNotificationsData = await NotificationService.getAllUnreadNotifications(userId);

      const formattedUnreadNotifications = unreadNotificationsData.map(notification => {
        try {
          return NotificationService.formatNotificationForUI(notification);
        } catch (formatError) {
          return {
            ...notification,
            notificationId: notification.id || notification.notificationId,
            read: false
          };
        }
      });

      return formattedUnreadNotifications;
    } catch (err) {
      logger.error('Failed to fetch unread notifications', { error: err.message, userId });
      throw err;
    }
  }, [userId]);

  const handleMarkAsRead = useCallback(async (notificationId) => {
    if (!userId || !notificationId) {
      return;
    }

    try {
      await NotificationService.markAsRead(userId, notificationId);

      // Update local state immediately for better UX
      setNotifications(prev =>
        prev.map(notification =>
          notification.notificationId === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );

      // Invalidate cache and refresh in background
      organizationCache.invalidate('notifications', userId);
    } catch (error) {
      // Revert optimistic update on error
      setNotifications(prev =>
        prev.map(notification =>
          notification.notificationId === notificationId
            ? { ...notification, read: false }
            : notification
        )
      );
      throw error;
    }
  }, [userId]);

  const handleMarkAllAsRead = useCallback(async () => {
    if (!userId) return;

    const hasUnread = notifications.some(n => !n.read);
    if (!hasUnread) return;

    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));

    try {
      await NotificationService.markAllAsRead(userId);
      organizationCache.invalidate('notifications', userId);
    } catch (error) {
      // Revert on failure
      await fetchNotifications(true);
      throw error;
    }
  }, [userId, notifications, fetchNotifications]);

  const handleDeleteNotification = useCallback(async (notificationId) => {
    if (!userId || !notificationId) {
      return;
    }

    try {
      // Optimistic update
      setNotifications(prev =>
        prev.filter(notification => notification.notificationId !== notificationId)
      );

      await NotificationService.deleteNotification(userId, notificationId);

      // Invalidate cache
      organizationCache.invalidate('notifications', userId);
    } catch (error) {
      // Revert optimistic update on error
      await fetchNotifications(true);
      throw error;
    }
  }, [userId, fetchNotifications]);

  const handleCreateNotification = useCallback(async (notificationData) => {
    if (!userId) {
      return;
    }

    try {
      const apiNotificationData = NotificationService.formatNotificationForAPI(notificationData);

      const createdNotification = await NotificationService.createNotification(userId, apiNotificationData);

      // Invalidate cache and refresh
      organizationCache.invalidate('notifications', userId);
      await fetchNotifications();

      return createdNotification;
    } catch (error) {
      throw error;
    }
  }, [userId, fetchNotifications]);

  // Listen for cache updates
  useEffect(() => {
    const handleCacheUpdate = (event) => {
      const { dataType, orgId, data } = event.detail;
      if (dataType === 'notifications' && orgId === userId) {
        const formattedNotifications = data.map(notification => {
          try {
            return NotificationService.formatNotificationForUI(notification);
          } catch (formatError) {
            return {
              ...notification,
              notificationId: notification.id || notification.notificationId,
              read: false
            };
          }
        });
        setNotifications(formattedNotifications);
        setLoading(false);
        setError(null);
      }
    };

    const handlePreload = (event) => {
      const { orgId } = event.detail;
      if (orgId === userId) {
        const cachedNotifications = organizationCache.get('notifications', userId);
        if (cachedNotifications) {
          const formattedNotifications = cachedNotifications.map(notification => {
            try {
              return NotificationService.formatNotificationForUI(notification);
            } catch (formatError) {
              return {
                ...notification,
                notificationId: notification.id || notification.notificationId,
                read: false
              };
            }
          });
          setNotifications(formattedNotifications);
          setLoading(false);
          setError(null);
        }
      }
    };

    window.addEventListener('organizationCacheUpdated', handleCacheUpdate);
    window.addEventListener('organizationDataPreloaded', handlePreload);

    return () => {
      window.removeEventListener('organizationCacheUpdated', handleCacheUpdate);
      window.removeEventListener('organizationDataPreloaded', handlePreload);
    };
  }, [userId]);

  // Initial load
  useEffect(() => {
    if (userId) {
      fetchNotifications();
    }
  }, [fetchNotifications]);

  // Auto-refresh notifications every 30 seconds
  useEffect(() => {
    if (!userId) return;

    const interval = setInterval(() => {
      fetchNotifications(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [userId, fetchNotifications]);

  return {
    notifications,
    loading,
    error,
    unreadCount,
    fetchNotifications,
    fetchUnreadNotifications,
    handleMarkAsRead,
    handleMarkAllAsRead,
    handleDeleteNotification,
    handleCreateNotification
  };
};
